import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertCircle, GripVertical, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import type { MetadataGroup } from './types';
import { updateOrderValues } from './utils';

interface SortableGroupItemProps {
  group: MetadataGroup;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onEdit: (group: MetadataGroup) => void;
  onDelete: () => void;
  hasFields: boolean;
  hasErrors: boolean;
}

function SortableGroupItem({
  group,
  isSelected,
  disabled,
  onSelect,
  onEdit,
  onDelete,
  hasFields,
  hasErrors,
}: SortableGroupItemProps) {
  const { t } = useIntl();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editLabelDe, setEditLabelDe] = useState(group.labelDe);
  const [editLabelEn, setEditLabelEn] = useState(group.labelEn || '');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: group.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleSaveEdit = () => {
    onEdit({
      ...group,
      labelDe: editLabelDe,
      labelEn: editLabelEn || undefined,
    });
    setIsEditOpen(false);
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: Using div for sortable drag-and-drop compatibility */}
      <div
        ref={setNodeRef}
        style={style}
        role="button"
        tabIndex={0}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg transition-all cursor-pointer group',
          isSelected
            ? hasErrors
              ? 'bg-destructive/10 text-destructive shadow-sm ring-1 ring-destructive'
              : 'bg-background text-primary shadow-sm ring-1 ring-border'
            : hasErrors
              ? 'text-destructive hover:bg-destructive/10'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          isDragging && 'opacity-50 border-2 border-dashed border-primary'
        )}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        {!disabled && (
          <span
            {...attributes}
            {...listeners}
            className={cn(
              'cursor-move',
              hasErrors
                ? 'text-destructive/50'
                : isSelected
                  ? 'text-primary/50'
                  : 'text-muted-foreground/50'
            )}
          >
            <GripVertical size={14} />
          </span>
        )}
        <span className="truncate flex-1 text-sm font-medium">{group.labelDe}</span>
        {hasErrors && <AlertCircle className="size-3.5 text-destructive shrink-0" />}

        {!disabled && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Popover open={isEditOpen} onOpenChange={setIsEditOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground hover:text-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditLabelDe(group.labelDe);
                    setEditLabelEn(group.labelEn || '');
                  }}
                >
                  <Pencil size={12} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="labelDe">{t('common.label')} (DE)</Label>
                    <Input
                      id="labelDe"
                      value={editLabelDe}
                      onChange={(e) => setEditLabelDe(e.target.value)}
                      placeholder="Gruppenname"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="labelEn">{t('common.label')} (EN)</Label>
                    <Input
                      id="labelEn"
                      value={editLabelEn}
                      onChange={(e) => setEditLabelEn(e.target.value)}
                      placeholder="Group name"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setIsEditOpen(false)}>
                      <X size={14} className="mr-1" />
                      {t('common.cancel')}
                    </Button>
                    <Button size="sm" onClick={handleSaveEdit}>
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <Button
              variant="ghost"
              size="icon"
              className="size-6 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                setIsDeleteDialogOpen(true);
              }}
            >
              <Trash2 size={12} />
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('metadata.groups.deleteGroup')}</AlertDialogTitle>
            <AlertDialogDescription>
              {hasFields
                ? t('metadata.builder.deleteGroupWithFieldsWarning', {
                    name: group.labelDe,
                  })
                : t('metadata.groups.deleteGroupConfirmation', {
                    name: group.labelDe,
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface MetadataGroupSidebarProps {
  groups: MetadataGroup[];
  selectedGroupId: string | null;
  onSelectGroup: (id: string) => void;
  onReorderGroups: (groups: MetadataGroup[]) => void;
  onAddGroup: (labelDe: string, labelEn?: string) => void;
  onEditGroup: (group: MetadataGroup) => void;
  onDeleteGroup: (id: string) => void;
  disabled?: boolean;
  fieldsPerGroup: Record<string, number>;
  /** Set of group IDs that have fields with errors */
  groupsWithErrors?: Set<string>;
}

export function MetadataGroupSidebar({
  groups,
  selectedGroupId,
  onSelectGroup,
  onReorderGroups,
  onAddGroup,
  onEditGroup,
  onDeleteGroup,
  disabled = false,
  fieldsPerGroup,
  groupsWithErrors = new Set(),
}: MetadataGroupSidebarProps) {
  const { t } = useIntl();
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [newGroupLabelDe, setNewGroupLabelDe] = useState('');
  const [newGroupLabelEn, setNewGroupLabelEn] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = groups.findIndex((g) => g.id === active.id);
      const newIndex = groups.findIndex((g) => g.id === over.id);
      const reordered = arrayMove(groups, oldIndex, newIndex);
      onReorderGroups(updateOrderValues(reordered));
    }
  };

  const handleAddGroup = () => {
    if (!newGroupLabelDe.trim()) return;

    onAddGroup(newGroupLabelDe.trim(), newGroupLabelEn.trim() || undefined);

    setNewGroupLabelDe('');
    setNewGroupLabelEn('');
    setIsAddingGroup(false);
  };

  const handleOpenAddDialog = () => {
    setNewGroupLabelDe('');
    setNewGroupLabelEn('');
    setIsAddingGroup(true);
  };

  return (
    <div className="w-56 border-r bg-muted/30 flex flex-col overflow-y-auto">
      <div className="h-11 px-5 border-b flex items-center shrink-0">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t('metadata.builder.groups')}
        </h3>
      </div>

      <div className="flex-1 p-3 space-y-1">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={groups.map((g) => g.id)} strategy={verticalListSortingStrategy}>
            {groups.map((group) => (
              <SortableGroupItem
                key={group.id}
                group={group}
                isSelected={selectedGroupId === group.id}
                disabled={disabled}
                onSelect={() => onSelectGroup(group.id)}
                onEdit={onEditGroup}
                onDelete={() => onDeleteGroup(group.id)}
                hasFields={(fieldsPerGroup[group.id] || 0) > 0}
                hasErrors={groupsWithErrors.has(group.id)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Add Group Button */}
        {!disabled && (
          <Popover open={isAddingGroup} onOpenChange={setIsAddingGroup}>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={handleOpenAddDialog}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors border-2 border-dashed border-muted hover:border-primary/30"
              >
                <Plus size={14} />
                <span className="text-sm">{t('metadata.groups.addGroup')}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="start">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="newLabelDe">{t('common.label')} (DE)</Label>
                  <Input
                    id="newLabelDe"
                    value={newGroupLabelDe}
                    onChange={(e) => setNewGroupLabelDe(e.target.value)}
                    placeholder="Gruppenname"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="newLabelEn">{t('common.label')} (EN)</Label>
                  <Input
                    id="newLabelEn"
                    value={newGroupLabelEn}
                    onChange={(e) => setNewGroupLabelEn(e.target.value)}
                    placeholder="Group name"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setIsAddingGroup(false)}>
                    <X size={14} className="mr-1" />
                    {t('common.cancel')}
                  </Button>
                  <Button size="sm" onClick={handleAddGroup} disabled={!newGroupLabelDe.trim()}>
                    {t('metadata.groups.createGroup')}
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
